import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Settings as SettingsIcon,
  Bell,
  Globe,
  Palette,
  Image,
  Shield,
  Upload,
  Loader2,
  CreditCard,
  Lock,
  Mail,
  Crown,
  Code,
  Plug,
} from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/stores/authStore';
import { useEnterpriseBranding } from '@/hooks/useEnterpriseBranding';
import { useSubscription } from '@/hooks/useSubscription';
import { DNSVerificationStatus } from '@/components/settings/DNSVerificationStatus';
import { BillingSection } from '@/components/settings/BillingSection';
import { PrivacySection } from '@/components/settings/PrivacySection';
import { SecuritySection } from '@/components/settings/SecuritySection';
import { ApiKeysSection } from '@/components/settings/ApiKeysSection';
import { WebhooksSection } from '@/components/settings/WebhooksSection';
import { IntegrationsSection } from '@/components/settings/IntegrationsSection';
import { NotificationSettings } from '@/components/settings/NotificationSettings';
import { toast } from 'sonner';

export default function Settings() {
  const { user, isLoading: authLoading } = useAuthStore();
  const {
    branding,
    isLoading,
    saveBranding,
    setCustomDomain,
    verifyDomain,
    removeDomain,
    uploadAsset,
  } = useEnterpriseBranding();
  const { limits } = useSubscription();
  const navigate = useNavigate();
  
  const hasWhiteLabelAccess = limits.features.whiteLabel;

  const [domainInput, setDomainInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingType, setUploadingType] = useState<string | null>(null);

  const logoInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);
  const redirectLogoInputRef = useRef<HTMLInputElement>(null);

  if (authLoading || isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  const handleDomainSubmit = async () => {
    if (!domainInput.trim()) return;
    
    try {
      await setCustomDomain(domainInput);
      setDomainInput('');
    } catch (error) {
      toast.error('Failed to add domain');
    }
  };

  const handleFileUpload = async (
    file: File,
    type: 'logo' | 'favicon' | 'redirect-logo' | 'email-logo',
    fieldKey: 'companyLogoUrl' | 'faviconUrl' | 'redirectPageLogoUrl' | 'emailHeaderLogoUrl'
  ) => {
    setUploadingType(type);
    try {
      const url = await uploadAsset(file, type);
      await saveBranding({ [fieldKey]: url });
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploadingType(null);
    }
  };

  const handleColorChange = async (field: string, value: string) => {
    setIsSaving(true);
    try {
      await saveBranding({ [field]: value });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 md:py-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-2xl md:text-3xl font-black tracking-tight flex items-center gap-3">
            <SettingsIcon className="h-7 w-7 text-primary" />
            Settings
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage your subscription, billing, and enterprise settings
          </p>
        </motion.div>

        <Tabs defaultValue="billing" className="space-y-6">
          <TabsList className="grid grid-cols-10 h-auto p-1.5 bg-muted/50 rounded-lg gap-1 w-full">
            <TabsTrigger 
              value="billing" 
              className="flex items-center justify-center gap-2 px-3 py-2 rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all whitespace-nowrap"
            >
              <CreditCard className="h-4 w-4" />
              <span className="hidden sm:inline">Billing</span>
            </TabsTrigger>
            <TabsTrigger 
              value="notifications" 
              className="flex items-center justify-center gap-2 px-3 py-2 rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all whitespace-nowrap"
            >
              <Bell className="h-4 w-4" />
              <span className="hidden sm:inline">Notifications</span>
            </TabsTrigger>
            <TabsTrigger 
              value="security" 
              className="flex items-center justify-center gap-2 px-3 py-2 rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all whitespace-nowrap"
            >
              <Shield className="h-4 w-4" />
              <span className="hidden sm:inline">Security</span>
            </TabsTrigger>
            <TabsTrigger 
              value="privacy" 
              className="flex items-center justify-center gap-2 px-3 py-2 rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all whitespace-nowrap"
            >
              <Lock className="h-4 w-4" />
              <span className="hidden sm:inline">Privacy</span>
            </TabsTrigger>
            <TabsTrigger 
              value="developer" 
              className="flex items-center justify-center gap-2 px-3 py-2 rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all whitespace-nowrap"
            >
              <Code className="h-4 w-4" />
              <span className="hidden sm:inline">Developer</span>
            </TabsTrigger>
            <TabsTrigger 
              value="integrations" 
              className="flex items-center justify-center gap-2 px-3 py-2 rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all whitespace-nowrap"
            >
              <Plug className="h-4 w-4" />
              <span className="hidden sm:inline">Integrations</span>
            </TabsTrigger>
            <TabsTrigger 
              value="domain" 
              className="flex items-center justify-center gap-2 px-3 py-2 rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all whitespace-nowrap"
            >
              <Globe className="h-4 w-4" />
              <span className="hidden sm:inline">Domain</span>
            </TabsTrigger>
            <TabsTrigger 
              value="branding" 
              className="flex items-center justify-center gap-2 px-3 py-2 rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all whitespace-nowrap"
            >
              <Palette className="h-4 w-4" />
              <span className="hidden sm:inline">Branding</span>
            </TabsTrigger>
            <TabsTrigger 
              value="assets" 
              className="flex items-center justify-center gap-2 px-3 py-2 rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all whitespace-nowrap"
            >
              <Image className="h-4 w-4" />
              <span className="hidden sm:inline">Assets</span>
            </TabsTrigger>
            <TabsTrigger 
              value="whitelabel" 
              className="flex items-center justify-center gap-2 px-3 py-2 rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all whitespace-nowrap"
            >
              <Mail className="h-4 w-4" />
              <span className="hidden sm:inline">White-Label</span>
            </TabsTrigger>
          </TabsList>

          {/* Developer Tab */}
          <TabsContent value="developer">
            <div className="space-y-6">
              <ApiKeysSection />
              <WebhooksSection />
            </div>
          </TabsContent>

          {/* Integrations Tab */}
          <TabsContent value="integrations">
            <IntegrationsSection />
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <NotificationSettings />
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing">
            <BillingSection />
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <SecuritySection />
          </TabsContent>

          {/* Privacy Tab */}
          <TabsContent value="privacy">
            <PrivacySection />
          </TabsContent>

          {/* Custom Domain Tab */}
          <TabsContent value="domain">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Custom Domain
                </CardTitle>
                <CardDescription>
                  Use your own domain for QR code short URLs (e.g., qr.yourbrand.com/r/abc123)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {branding?.customDomain ? (
                  <DNSVerificationStatus
                    domain={branding.customDomain}
                    domainVerified={branding.domainVerified}
                    domainVerificationToken={branding.domainVerificationToken || ''}
                    lastVerificationCheck={branding.lastVerificationCheckAt}
                    verificationErrors={branding.verificationErrors}
                    onVerify={verifyDomain}
                    onRemoveDomain={removeDomain}
                  />
                ) : (
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <Input
                        placeholder="qr.yourbrand.com"
                        value={domainInput}
                        onChange={(e) => setDomainInput(e.target.value)}
                      />
                      <Button onClick={handleDomainSubmit}>
                        Add Domain
                      </Button>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Enter a domain or subdomain you own. You'll need to configure DNS records.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Branding Colors Tab */}
          <TabsContent value="branding">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="h-5 w-5" />
                  Brand Colors
                </CardTitle>
                <CardDescription>
                  Customize colors for your QR redirect pages and emails
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Primary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={branding?.primaryColor || '#667eea'}
                        onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                        className="w-16 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={branding?.primaryColor || '#667eea'}
                        onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                        className="font-mono"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Secondary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={branding?.secondaryColor || '#764ba2'}
                        onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                        className="w-16 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={branding?.secondaryColor || '#764ba2'}
                        onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                        className="font-mono"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Background Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={branding?.backgroundColor || '#ffffff'}
                        onChange={(e) => handleColorChange('backgroundColor', e.target.value)}
                        className="w-16 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={branding?.backgroundColor || '#ffffff'}
                        onChange={(e) => handleColorChange('backgroundColor', e.target.value)}
                        className="font-mono"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Text Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={branding?.textColor || '#1a1a2e'}
                        onChange={(e) => handleColorChange('textColor', e.target.value)}
                        className="w-16 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={branding?.textColor || '#1a1a2e'}
                        onChange={(e) => handleColorChange('textColor', e.target.value)}
                        className="font-mono"
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-4">
                  <h4 className="font-medium">Preview</h4>
                  <div
                    className="p-6 rounded-lg"
                    style={{
                      background: `linear-gradient(135deg, ${branding?.primaryColor || '#667eea'} 0%, ${branding?.secondaryColor || '#764ba2'} 100%)`,
                    }}
                  >
                    <div
                      className="p-4 rounded-lg"
                      style={{ backgroundColor: branding?.backgroundColor || '#ffffff' }}
                    >
                      <p style={{ color: branding?.textColor || '#1a1a2e' }}>
                        Sample text with your brand colors
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Assets Tab */}
          <TabsContent value="assets">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="h-5 w-5" />
                  Brand Assets
                </CardTitle>
                <CardDescription>
                  Upload your company logo and favicon
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Company Logo */}
                <div className="space-y-3">
                  <Label>Company Logo</Label>
                  <p className="text-sm text-muted-foreground">
                    Replaces the app logo in your dashboard
                  </p>
                  <div className="flex items-center gap-4">
                    {branding?.companyLogoUrl ? (
                      <img
                        src={branding.companyLogoUrl}
                        alt="Company logo"
                        className="h-16 w-auto object-contain border rounded-lg p-2"
                      />
                    ) : (
                      <div className="h-16 w-32 border-2 border-dashed rounded-lg flex items-center justify-center text-muted-foreground">
                        No logo
                      </div>
                    )}
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(file, 'logo', 'companyLogoUrl');
                      }}
                    />
                    <Button
                      variant="outline"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingType === 'logo'}
                    >
                      {uploadingType === 'logo' ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Upload className="h-4 w-4 mr-2" />
                      )}
                      Upload Logo
                    </Button>
                  </div>
                </div>

                <Separator />

                {/* Favicon */}
                <div className="space-y-3">
                  <Label>Favicon</Label>
                  <p className="text-sm text-muted-foreground">
                    Shown in browser tabs (recommended: 32x32 or 64x64 PNG)
                  </p>
                  <div className="flex items-center gap-4">
                    {branding?.faviconUrl ? (
                      <img
                        src={branding.faviconUrl}
                        alt="Favicon"
                        className="h-8 w-8 object-contain border rounded"
                      />
                    ) : (
                      <div className="h-8 w-8 border-2 border-dashed rounded flex items-center justify-center text-muted-foreground text-xs">
                        —
                      </div>
                    )}
                    <input
                      ref={faviconInputRef}
                      type="file"
                      accept="image/png,image/ico,image/x-icon"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(file, 'favicon', 'faviconUrl');
                      }}
                    />
                    <Button
                      variant="outline"
                      onClick={() => faviconInputRef.current?.click()}
                      disabled={uploadingType === 'favicon'}
                    >
                      {uploadingType === 'favicon' ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Upload className="h-4 w-4 mr-2" />
                      )}
                      Upload Favicon
                    </Button>
                  </div>
                </div>

                <Separator />

                {/* Redirect Page Logo */}
                <div className="space-y-3">
                  <Label>Redirect Page Logo</Label>
                  <p className="text-sm text-muted-foreground">
                    Shown on QR redirect pages (vCard, WiFi info, etc.)
                  </p>
                  <div className="flex items-center gap-4">
                    {branding?.redirectPageLogoUrl ? (
                      <img
                        src={branding.redirectPageLogoUrl}
                        alt="Redirect page logo"
                        className="h-12 w-auto object-contain border rounded-lg p-2"
                      />
                    ) : (
                      <div className="h-12 w-24 border-2 border-dashed rounded-lg flex items-center justify-center text-muted-foreground text-xs">
                        No logo
                      </div>
                    )}
                    <input
                      ref={redirectLogoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(file, 'redirect-logo', 'redirectPageLogoUrl');
                      }}
                    />
                    <Button
                      variant="outline"
                      onClick={() => redirectLogoInputRef.current?.click()}
                      disabled={uploadingType === 'redirect-logo'}
                    >
                      {uploadingType === 'redirect-logo' ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Upload className="h-4 w-4 mr-2" />
                      )}
                      Upload
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* White-Label Tab */}
          <TabsContent value="whitelabel">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  White-Label Options
                  {!hasWhiteLabelAccess && (
                    <Badge variant="secondary" className="ml-2">
                      <Crown className="h-3 w-3 mr-1" />
                      Business+
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Remove NexQR branding and customize messaging
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {!hasWhiteLabelAccess ? (
                  <div className="text-center py-8 space-y-4">
                    <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                      <Crown className="h-8 w-8 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">Upgrade to Business</h3>
                      <p className="text-muted-foreground mt-1">
                        White-label options are available on Business and Enterprise plans.
                      </p>
                    </div>
                    <Button onClick={() => navigate('/pricing')}>
                      View Plans
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-muted/30 rounded-lg">
                      <div>
                        <Label className="font-medium">Remove "Powered by NexQR"</Label>
                        <p className="text-sm text-muted-foreground">
                          Hide the NexQR attribution from redirect pages
                        </p>
                      </div>
                      <Switch
                        checked={branding?.removePoweredBy || false}
                        onCheckedChange={(checked) => saveBranding({ removePoweredBy: checked })}
                      />
                    </div>

                    <Separator />

                    <div className="space-y-3">
                      <Label>Company Name</Label>
                      <Input
                        placeholder="Your Company Name"
                        value={branding?.companyName || ''}
                        onChange={(e) => saveBranding({ companyName: e.target.value })}
                      />
                      <p className="text-sm text-muted-foreground">
                        Shown in redirect pages and emails
                      </p>
                    </div>

                    <Separator />

                    <div className="space-y-3">
                      <Label>Custom 404 Message</Label>
                      <Textarea
                        placeholder="This QR code doesn't exist or has been deleted."
                        value={branding?.custom404Message || ''}
                        onChange={(e) => saveBranding({ custom404Message: e.target.value })}
                        rows={2}
                      />
                    </div>

                    <div className="space-y-3">
                      <Label>Custom Expired/Paused Message</Label>
                      <Textarea
                        placeholder="This QR code has been temporarily disabled."
                        value={branding?.customExpiredMessage || ''}
                        onChange={(e) => saveBranding({ customExpiredMessage: e.target.value })}
                        rows={2}
                      />
                    </div>

                    <Separator />

                    <div className="space-y-3">
                      <h4 className="font-medium flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        Email Branding
                      </h4>
                      
                      <div className="space-y-3">
                        <Label>Email From Name</Label>
                        <Input
                          placeholder="Your Company"
                          value={branding?.emailFromName || ''}
                          onChange={(e) => saveBranding({ emailFromName: e.target.value })}
                        />
                      </div>

                      <div className="space-y-3">
                        <Label>Email Footer Text</Label>
                        <Textarea
                          placeholder="© 2024 Your Company. All rights reserved."
                          value={branding?.emailFooterText || ''}
                          onChange={(e) => saveBranding({ emailFooterText: e.target.value })}
                          rows={2}
                        />
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          {/* Developer Tab */}
          <TabsContent value="developer">
            <ApiKeysSection />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
